import React from 'react';
import { RecommendationData } from './useRecommendation';
import { ReactComponent as Trash } from 'assets/icons/trash.svg';
import testKitImg from 'assets/images/test-kit-images/img.svg';
import moment from 'moment/moment';
import { STANDARD_DATE } from 'utilities/dateHelper';
import ButtonComponent from 'components/button/buttonComponent';
import CustomTooltip from 'components/CustomTooltip';
import { twMerge } from 'tailwind-merge';
import { ServiceRequestStatus } from 'graphql/generated/hasura';

interface IRecommendationItemDesktop {
  recommendation: RecommendationData;
  containerClassNames?: string;
  showImage?: boolean;
  locale: Record<string, string>;
  handleNameClick: () => void;
  moveToArchive?: () => void;
  deleteRecommendation?: () => void;
  purchaseRecommendation: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

const removeStatuses = [
  ServiceRequestStatus.OnHold,
  ServiceRequestStatus.Completed,
];

export const RecommendationItemDesktop: React.FC<
  IRecommendationItemDesktop
> = ({
  recommendation,
  showImage = false,
  containerClassNames = 'rounded-10 bg-white w-full border border-graph-border-color py-4 px-5',
  locale,
  handleNameClick,
  moveToArchive,
  deleteRecommendation,
  purchaseRecommendation,
}) => {
  return (
    <div
      className="hidden desktop:block w-full"
      data-testid="upcoming-appointment-card-desktop"
      onClick={(e) => e.stopPropagation()}
    >
      <div className={containerClassNames}>
        <div className="flex w-full justify-between">
          <div className="flex gap-4">
            {showImage && (
              <div className="overflow-hidden rounded-5 w-[42px] h-[42px] bg-cover bg-center">
                <img
                  src={recommendation.imageUrl || testKitImg}
                  className="object-cover w-full h-full"
                  alt={`${recommendation.productTitle}-image`}
                />
              </div>
            )}

            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2.5">
                <div className="text-base text-btn-black font-semibold">
                  {recommendation.productTitle}
                </div>
                <div className="text-sm font-medium text-med-gray-3">
                  {locale.quantity} {recommendation.quantity}{' '}
                  {Number(recommendation.quantity) > 1
                    ? locale.tests
                    : locale.test}
                </div>
              </div>

              <div className="flex items-center text-xs text-med-gray-3 gap-2.5">
                <div>
                  {moment(recommendation.createdAt).format(STANDARD_DATE)}
                </div>

                <span className="text-[6px]">●</span>

                <div className="flex items-center">
                  <div>{locale.provider}</div>

                  <ButtonComponent
                    type="underline"
                    onClick={handleNameClick}
                    noUpperCase
                    testID={'provider-name'}
                    className="ml-1 !text-clc-blue !text-xs whitespace-nowrap"
                  >
                    {recommendation.providerName}
                  </ButtonComponent>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-4">
            {recommendation.isPurchased && (
              <span className="capitalize inline-block text-sm font-semibold border px-3 min-w-[6rem] text-center rounded-full text-bright-green border-bright-green bg-green-50">
                {locale.purchased}
              </span>
            )}

            <div
              className={twMerge(
                'hidden text-sm font-semibold w-fit text-bright-green border rounded-full border-bright-green bg-green-50 px-2.5',
                recommendation.hasInCart && 'block',
              )}
            >
              Added to cart
            </div>

            <div className="text-base text-btn-black font-semibold">
              {locale.total}: ${(recommendation.totalPrice / 100).toFixed(2)}
            </div>

            <ButtonComponent
              disabled={recommendation.hasInCart}
              dataTooltipId={
                recommendation.hasInCart ? recommendation.id : undefined
              }
              testID="purchase-button"
              className={twMerge(recommendation.hasInCart && 'hidden')}
              onClick={purchaseRecommendation}
            >
              Add to cart
            </ButtonComponent>

            {recommendation.status === ServiceRequestStatus.Active &&
              moveToArchive && (
                <ButtonComponent
                  testID="move-to-archive-button"
                  onClick={moveToArchive}
                  className={twMerge(
                    'group bg-transparent px-1.5 -ml-2.5',
                    recommendation.showArchive && 'hidden',
                  )}
                  type="icon"
                  Icon={() => (
                    <Trash className="w-5 h-5 min-w-[20px] fill-med-gray-3 group-hover:fill-white " />
                  )}
                />
              )}

            {(recommendation.showArchive ||
              removeStatuses.includes(recommendation.status)) &&
              deleteRecommendation && (
                <ButtonComponent
                  testID="move-to-archive-button"
                  onClick={deleteRecommendation}
                  className={twMerge('group bg-transparent px-1.5 -ml-2.5')}
                  type="icon"
                  Icon={() => (
                    <Trash className="w-5 h-5 min-w-[20px] fill-med-gray-3 group-hover:fill-white " />
                  )}
                />
              )}

            <CustomTooltip place="top" id={recommendation.id}>
              In cart right now
            </CustomTooltip>
          </div>
        </div>
      </div>
    </div>
  );
};
